import React, { useState, useEffect, useMemo } from 'react';
import teacherService from '../../services/api/teacher';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getArabicDate } from '../../services/utils/date';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useQuestionsSearch } from '../../hooks/useSearch';
import { 
  MessageCircle, 
  Users, 
  Clock, 
  CheckCircle, 
  Send, 
  Search, 
  Filter, 
  ShieldCheck, 
  ChevronLeft, 
  Sparkles, 
  HelpCircle, 
  Layers, 
  BookOpen,
  UserCheck,
  Zap,
  Mail,
  ChevronDown
} from 'lucide-react';
import { useToast } from '../../components/common/Toast';

const TeacherQuestions = () => {
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedLesson, setSelectedLesson] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedStage, setSelectedStage] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [answerText, setAnswerText] = useState('');
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const toast = useToast();
  const queryClient = useQueryClient();

  // جلب بيانات الفلاتر الخاصة بالمدرس
  const { data: teacherSubjectsData } = useQuery(
    'teacher-subjects',
    () => teacherService.getSubjects(),
    {
      retry: false,
      staleTime: 300000,
      refetchOnWindowFocus: false
    }
  );

  // Extract unique stages and grades from teacher's subjects
  const filters = useMemo(() => {
    // Check if teacherSubjectsData is directly an array or has subjects property
    const subjectsArray = Array.isArray(teacherSubjectsData) ? teacherSubjectsData : teacherSubjectsData?.subjects || [];
    
    if (!subjectsArray || subjectsArray.length === 0) {
      return { stages: [], subjects: [], grades: [], courses: [] };
    }

    const stagesSet = new Set();
    const gradesSet = new Set();
    const coursesSet = new Set();
    const stages = [];
    const grades = [];
    const courses = [];

    subjectsArray.forEach(subject => {
      // Add stage if not already added - use subject_id as stage_id since stage_id is undefined
      if (subject.subject_id && !stagesSet.has(subject.subject_id)) {
        stagesSet.add(subject.subject_id);
        stages.push({
          id: subject.subject_id,
          name: subject.stage_name
        });
      }

      // Add grade if not already added - use subject_id as stage_id for grade
      if (subject.grade_id && !gradesSet.has(subject.grade_id)) {
        gradesSet.add(subject.grade_id);
        grades.push({
          id: subject.grade_id,
          name: subject.grade_name,
          stage_id: subject.subject_id // Use subject_id as stage_id
        });
      }

      // Add course if not already added
      if (subject.course_id && !coursesSet.has(subject.course_id)) {
        coursesSet.add(subject.course_id);
        courses.push({
          id: subject.course_id,
          title: subject.course_title,
          subject_id: subject.teacher_subject_id,
          subject_name: subject.subject_name
        });
      }
    });

    return {
      stages: stages.sort((a, b) => a.name.localeCompare(b.name)),
      subjects: subjectsArray.map(s => ({
        id: s.teacher_subject_id,
        name: s.subject_name,
        grade_name: s.grade_name,
        course_title: s.course_title
      })).sort((a, b) => a.name.localeCompare(b.name)),
      grades: grades.sort((a, b) => a.name.localeCompare(b.name)),
      courses: courses.sort((a, b) => a.title.localeCompare(b.title))
    };
  }, [teacherSubjectsData]);

  // جلب الأسئلة بدون فلترة (سنقوم بالفلترة محلياً)
  const { data: questionsData, isLoading } = useQuery(
    'teacher-questions',
    () => teacherService.getTeacherQuestions({}),
    {
      retry: false,
      staleTime: 30000,
      refetchOnWindowFocus: false
    }
  );
  
  const questions = questionsData?.questions || [];

  // جلب قائمة الدروس للفلاتر
  const { data: lessonsData } = useQuery(
    'teacher-lessons',
    () => teacherService.getTeacherLessonsOriginal('')
  );

  // Extract courses based on selected subject
  const allCourses = useMemo(() => {
    if (!selectedSubject) return filters?.courses || [];
    return filters?.courses?.filter(course => course.subject_id === parseInt(selectedSubject)) || [];
  }, [selectedSubject, filters?.courses]);

  // Extract lessons based on selected course
  const allLessons = useMemo(() => {
    if (!selectedCourse) return lessonsData?.lessons || [];
    return (lessonsData?.lessons || []).filter(lesson => lesson.course_id === parseInt(selectedCourse));
  }, [selectedCourse, lessonsData?.lessons]);

  // Extract grades - return all grades (not filtered by any selection)
  const allGrades = useMemo(() => {
    return filters?.grades || [];
  }, [filters?.grades]);

  // استخدام الـ Hook الجديد للبحث والفلترة
  const {
    searchTerm: hookSearchTerm,
    filteredData: searchedQuestions,
    setSearchTerm: setHookSearchTerm,
    updateFilter
  } = useQuestionsSearch(questions);

  // Handle search input change
  const handleSearch = (e) => {
    setHookSearchTerm(e.target.value);
  };

  // Apply filters when they change
  useEffect(() => {
    // Apply status filter
    if (filterStatus === 'answered') {
      updateFilter('answered_at', true);
    } else if (filterStatus === 'unanswered') {
      updateFilter('answered_at', false);
    } else {
      updateFilter('answered_at', '');
    }
    
    // Apply lesson filter
    if (selectedLesson) {
      updateFilter('lesson_id', parseInt(selectedLesson));
    } else {
      updateFilter('lesson_id', '');
    }
    
    // Apply course filter
    if (selectedCourse) {
      updateFilter('course_id', parseInt(selectedCourse));
    } else {
      updateFilter('course_id', '');
    }
    
    // Apply subject filter
    if (selectedSubject) {
      updateFilter('teacher_subject_id', parseInt(selectedSubject));
    } else {
      updateFilter('teacher_subject_id', '');
    }
    
    // Apply stage filter
    if (selectedStage) {
      updateFilter('stage_id', parseInt(selectedStage));
    } else {
      updateFilter('stage_id', '');
    }
    
    // Apply grade filter
    if (selectedGrade) {
      updateFilter('grade_id', parseInt(selectedGrade));
    } else {
      updateFilter('grade_id', '');
    }
  }, [filterStatus, selectedLesson, selectedCourse, selectedSubject, selectedStage, selectedGrade, updateFilter]);

  // تنفيذ عملية الرد على السؤال (Mutation)
  const answerMutation = useMutation(
    ({ questionId, answerText, isPublic }) => 
      teacherService.answerQuestion(questionId, { answerText, isPublic }),
    {
      onSuccess: () => {
        toast.success('تم إرسال إجابتك بنجاح للطالب');
        queryClient.invalidateQueries('teacher-questions');
        setAnswerText('');
        setSelectedQuestion(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'فشل إرسال الإجابة، حاول مرة أخرى');
      }
    }
  );

  const handleAnswer = (questionId) => {
    if (!answerText.trim()) {
        toast.error('يرجى كتابة نص الإجابة أولاً');
        return;
    }
    
    answerMutation.mutate({
      questionId,
      answerText,
      isPublic: true
    });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 min-h-[500px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse uppercase tracking-widest text-xs">جاري فتح بنك الأسئلة...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-10 px-4 sm:px-6 py-6 min-h-screen bg-gray-50/30">
      
      {/* Header Section */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-10 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-32 h-32 bg-primary-50 rounded-br-[5rem] opacity-40"></div>
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-1">
            <h1 className="text-3xl lg:text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">
                <MessageCircle className="text-primary-600" size={32} />
                غرفة استفسارات الطلاب
            </h1>
            <p className="text-gray-500 font-medium flex items-center gap-2">
                <ShieldCheck size={18} className="text-emerald-500" />
                تفاعل مع طلابك، أجب على تساؤلاتهم وابنِ مجتمعاً تعليمياً ناجحاً
            </p>
          </div>
          
          <div className="bg-primary-600 text-white px-8 py-4 rounded-2xl shadow-xl shadow-primary-100 flex items-center gap-4">
              <div className="bg-white/20 p-2 rounded-xl"><UserCheck size={24} /></div>
              <div>
                  <p className="text-[10px] font-black uppercase opacity-80 leading-none mb-1">إجمالي الأسئلة</p>
                  <p className="text-2xl font-black leading-none">{questions.length || 0}</p>
              </div>
          </div>
        </div>
      </div>

      {/* Stats Dashboard */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { label: 'أسئلة بانتظار الرد', value: questions.filter(q => !q.answered_at).length, icon: Zap, color: 'text-amber-600', bg: 'bg-amber-50', urgent: true },
          { label: 'أسئلة تم إجابتها', value: questions.filter(q => q.answered_at).length, icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'الدروس المتفاعلة', value: new Set(questions.map(q => q.lesson_title)).size, icon: Layers, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'الطلاب المستفسرين', value: new Set(questions.map(q => q.student_name)).size, icon: Users, color: 'text-purple-600', bg: 'bg-purple-50' }
        ].map((stat, i) => (
          <div key={i} className={`bg-white p-6 rounded-[2rem] border shadow-sm flex items-center gap-5 group hover:shadow-md transition-all ${stat.urgent && stat.value > 0 ? 'border-amber-200 ring-4 ring-amber-50' : 'border-gray-100'}`}>
            <div className={`p-4 ${stat.bg} ${stat.color} rounded-2xl group-hover:scale-110 transition-transform`}>
              <stat.icon size={24} />
            </div>
            <div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">{stat.label}</p>
              <p className="text-2xl font-black text-gray-900 leading-none">{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Advanced Filters Bar */}
      <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-6 overflow-visible relative z-40">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
          
          {/* Main Search Input */}
          <div className="relative md:col-span-5 group">
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500 transition-colors w-5 h-5" />
            <input
              type="text"
              placeholder="ابحث عن سؤال محدد أو اسم طالب..."
              value={hookSearchTerm}
              onChange={handleSearch}
              className="w-full pr-12 pl-4 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-medium"
            />
          </div>
          
          {/* Status Filter Dropdown */}
          <div className="md:col-span-3">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all font-bold text-gray-700 appearance-none cursor-pointer"
            >
              <option value="all">كل الحالات</option>
              <option value="answered">تمت الإجابة</option>
              <option value="unanswered">لم تتم الإجابة</option>
            </select>
          </div>

          {/* Advanced Filters Button */}
          <div className="md:col-span-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`w-full flex items-center justify-center gap-2 py-4 border rounded-2xl font-bold transition-all ${showFilters ? 'bg-primary-50 border-primary-200 text-primary-600' : 'bg-white border-gray-100 text-gray-500 hover:bg-gray-50'}`}
            >
              <Filter size={18} />
              الفلاتر
              <ChevronDown size={18} className={`transition-transform duration-300 ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          <div className="md:col-span-2">
            <div className="bg-primary-600 text-white py-4 rounded-2xl text-center font-black text-xs uppercase tracking-widest shadow-lg shadow-primary-100">
                {searchedQuestions?.length || 0} سؤال مـسـجـل
            </div>
          </div>
        </div>

        {/* Expandable Advanced Filters */}
        {showFilters && (
          <div className="mt-6 pt-6 border-t border-gray-50 grid grid-cols-1 md:grid-cols-3 gap-4
                          animate-in fade-in slide-in-from-top duration-300
                          relative z-50 overflow-visible">
            <div className="space-y-2 relative overflow-visible">
                <label className="text-[10px] font-black text-gray-400 uppercase mr-2 tracking-widest">المرحلة الدراسية</label>
                <div className="relative overflow-visible">
                    <select
                    value={selectedStage}
                    onChange={(e) => {
                      setSelectedStage(e.target.value);
                      setSelectedGrade(''); // Reset grade when stage changes
                    }}
                    className="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:bg-white transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                    >
                    <option value="">جميع المراحل</option>
                    {filters?.stages?.map((stage) => (
                        <option key={stage.id} value={stage.id}>{stage.name}</option>
                    ))}
                    </select>
                </div>
            </div>

            <div className="space-y-2 relative overflow-visible">
                <label className="text-[10px] font-black text-gray-400 uppercase mr-2 tracking-widest">الصف الدراسي</label>
                <div className="relative overflow-visible">
                    <select
                    value={selectedGrade}
                    onChange={(e) => setSelectedGrade(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:bg-white transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                    >
                    <option value="">جميع الصفوف</option>
                    {allGrades?.map((grade) => (
                        <option key={grade.id} value={grade.id}>{grade.name}</option>
                    ))}
                    </select>
                </div>
            </div>

            <div className="space-y-2 relative overflow-visible">
                <label className="text-[10px] font-black text-gray-400 uppercase mr-2 tracking-widest">المادة العلمية</label>
                <div className="relative overflow-visible">
                    <select
                    value={selectedSubject}
                    onChange={(e) => {
                      setSelectedSubject(e.target.value);
                      setSelectedCourse(''); // Reset course when subject changes
                      setSelectedLesson(''); // Reset lesson when subject changes
                    }}
                    className="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:bg-white transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                    >
                    <option value="">جميع المواد</option>
                    {filters?.subjects?.map((subject) => (
                        <option key={subject.id} value={subject.id}>{subject.name}</option>
                    ))}
                    </select>
                </div>
            </div>

            <div className="space-y-2 relative overflow-visible">
                <label className="text-[10px] font-black text-gray-400 uppercase mr-2 tracking-widest">الكورس</label>
                <div className="relative overflow-visible">
                    <select
                    value={selectedCourse}
                    onChange={(e) => {
                      setSelectedCourse(e.target.value);
                      setSelectedLesson(''); // Reset lesson when course changes
                    }}
                    className="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:bg-white transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                    >
                    <option value="">جميع الكورسات</option>
                    {allCourses?.map((course) => (
                        <option key={course.id} value={course.id}>{course.title}</option>
                    ))}
                    </select>
                </div>
            </div>

            <div className="space-y-2 relative overflow-visible">
                <label className="text-[10px] font-black text-gray-400 uppercase mr-2 tracking-widest">الدرس</label>
                <div className="relative overflow-visible">
                    <select
                    value={selectedLesson}
                    onChange={(e) => setSelectedLesson(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:bg-white transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                    >
                    <option value="">جميع الدروس</option>
                    {allLessons?.map((lesson) => (
                        <option key={lesson.id} value={lesson.id}>{lesson.title}</option>
                    ))}
                    </select>
                </div>
            </div>
          </div>
        )}
      </div>

      {/* Questions Content List */}
      <div className="space-y-6 pb-20">
        {Array.isArray(searchedQuestions) && searchedQuestions.length === 0 ? (
          <div className="bg-white rounded-[3rem] p-20 text-center border border-dashed border-gray-200 shadow-sm">
            <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <HelpCircle size={48} className="text-gray-200" />
            </div>
            <h3 className="text-2xl font-black text-gray-900 mb-2">{hookSearchTerm ? 'لا توجد نتائج لبحثك' : 'لا توجد أسئلة حالياً'}</h3>
            <p className="text-gray-500 max-w-sm mx-auto font-medium">
              {hookSearchTerm ? 'جرب البحث بكلمات أخرى أو قم بإعادة ضبط الفلاتر.' : 'لم يقم الطلاب بطرح أي تساؤلات في المواد والدروس المحددة للبحث.'}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {Array.isArray(searchedQuestions) && searchedQuestions.map((question) => (
              <div 
                key={question.id} 
                id={`question-${question.id}`}
                className={`group bg-white rounded-[2.5rem] border transition-all duration-500 p-8 shadow-sm hover:shadow-xl ${!question.answered_at ? 'border-amber-100' : 'border-gray-100'}`}
              >
                {/* Question Header */}
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-8">
                  <div className="flex items-center gap-5">
                    <div className="relative flex-shrink-0">
                      <div className="w-16 h-16 rounded-3xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-black text-2xl shadow-lg shadow-primary-100 group-hover:scale-105 transition-transform overflow-hidden">
                          {question.student_name?.charAt(0).toUpperCase()}
                      </div>
                      {!question.answered_at && (
                          <div className="absolute -top-1 -left-1 w-4 h-4 bg-amber-500 rounded-full border-4 border-white animate-pulse"></div>
                      )}
                    </div>
                    <div>
                      <h4 className="text-lg font-black text-gray-900 leading-none mb-1.5 group-hover:text-primary-600 transition-colors">{question.student_name}</h4>
                      <div className="flex items-center gap-1 text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                          <Mail size={12} className="text-primary-400" /> {question.student_email}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-xs font-bold text-gray-400 uppercase tracking-widest bg-gray-50 px-4 py-2 rounded-xl">
                    <Clock size={16} className="text-primary-500" />
                    {getArabicDate(question.created_at)}
                  </div>
                </div>

                {/* Context Info */}
                <div className="flex flex-wrap items-center gap-2 mb-6">
                  <span className="bg-primary-50 text-primary-600 px-3 py-1 rounded-lg text-[10px] font-black border border-primary-100 uppercase tracking-widest flex items-center gap-1.5">
                      <BookOpen size={12} /> {question.course_title}
                  </span>
                  <ChevronLeft size={14} className="text-gray-300" />
                  <span className="bg-gray-100 text-gray-500 px-3 py-1 rounded-lg text-[10px] font-black border border-gray-200 uppercase tracking-widest">
                      {question.lesson_title}
                  </span>
                </div>

                {/* Question Text */}
                <div className="bg-gray-50/50 rounded-[2rem] p-6 lg:p-8 border border-gray-50 relative mb-8">
                    <div className="absolute -top-4 right-6 bg-white px-4 py-1 rounded-full border border-gray-100 text-[10px] font-black text-gray-400 uppercase tracking-widest">سؤال الطالب</div>
                    <p className="text-gray-700 leading-relaxed text-lg font-medium italic">
                      "{question.text}"
                    </p>
                </div>

                {/* Answer Content / Reply Form Logic */}
                {question.answered_at ? (
                  <div className="animate-in slide-in-from-bottom duration-500">
                      <div className="bg-emerald-50 border border-emerald-100 rounded-[2rem] p-6 lg:p-8 relative overflow-hidden group/reply">
                      <div className="absolute top-0 right-0 w-1.5 h-full bg-emerald-500 opacity-20 group-hover/reply:opacity-100 transition-opacity"></div>
                      <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                              <div className="bg-emerald-100 p-1.5 rounded-lg text-emerald-600">
                                  <CheckCircle2 size={16} />
                              </div>
                              <span className="text-[10px] font-black text-emerald-800 uppercase tracking-widest">إجابتك المعتمدة</span>
                          </div>
                          <span className="text-[9px] font-bold text-emerald-500 uppercase">{getArabicDate(question.answered_at)}</span>
                      </div>
                      <p className="text-emerald-900 leading-relaxed font-bold">{question.answer_text}</p>
                      </div>
                  </div>
                ) : (
                  <div className="border-t border-gray-50 pt-8 mt-4 animate-in fade-in duration-700">
                    {selectedQuestion === question.id ? (
                      <div className="space-y-5">
                        <div className="relative group">
                            <textarea
                              value={answerText}
                              onChange={(e) => setAnswerText(e.target.value)}
                              placeholder="اكتب ردك الأكاديمي والواضح هنا لمساعدة الطالب..."
                              className="w-full px-8 py-6 bg-gray-50 border border-gray-200 rounded-[2.5rem] focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 outline-none transition-all font-medium text-gray-800 shadow-inner resize-none leading-relaxed"
                              rows={5}
                            />
                            <div className="absolute -top-3 right-8 bg-primary-600 text-white px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg">صندوق الكتابة</div>
                        </div>
                        
                        <div className="flex flex-col sm:flex-row justify-end items-center gap-4">
                          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-auto">سيتم نشر الرد للطالب فور الضغط على إرسال</p>
                          <button
                            onClick={() => { setSelectedQuestion(null); setAnswerText(''); }}
                            className="w-full sm:w-auto px-8 py-3 rounded-2xl font-black text-sm text-gray-400 hover:bg-gray-100 transition-all"
                          >
                            تراجع
                          </button>
                          <button
                            onClick={() => handleAnswer(question.id)}
                            disabled={answerMutation.isLoading || !answerText.trim()}
                            className="w-full sm:w-auto px-10 py-3.5 bg-primary-600 text-white rounded-2xl font-black text-sm hover:bg-primary-700 transition-all shadow-xl shadow-primary-100 transform active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            {answerMutation.isLoading ? <LoadingSpinner size="small" /> : <Send size={18} />}
                            إرسال الرد المعتمد
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setSelectedQuestion(question.id)}
                        className="w-full py-4 bg-primary-50 text-primary-600 rounded-2xl font-black text-sm hover:bg-primary-600 hover:text-white transition-all shadow-sm flex items-center justify-center gap-2 transform active:scale-95 group"
                      >
                        <Zap size={18} className="group-hover:animate-bounce" fill="currentColor" />
                        الرد السريع على استفسار الطالب
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom Motivational Footer */}
      <div className="bg-gray-900 rounded-[3rem] p-10 text-white flex flex-col md:flex-row items-center justify-between gap-8 relative overflow-hidden group shadow-2xl">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-600/20 rounded-full blur-3xl -z-0"></div>
        <div className="relative z-10 text-center md:text-right space-y-2">
            <h3 className="text-2xl font-black flex items-center gap-2 justify-center md:justify-start leading-tight">
                <Sparkles className="text-primary-500" /> عزز تفاعلك التعليمي
            </h3>
            <p className="text-gray-400 font-medium max-w-xl">تذكر أن إجابتك السريعة والدقيقة تبني جداراً من الثقة بينك وبين طلابك وتساهم في تحسين تقييمات كورساتك.</p>
        </div>
        <button 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="relative z-10 bg-white text-gray-900 px-10 py-4 rounded-2xl font-black flex items-center gap-2 shadow-xl hover:bg-primary-50 transition-colors transform active:scale-95"
        >
            العودة للأعلى <ChevronLeft size={20} className="rotate-90" />
        </button>
      </div>
    </div>
  );
};

// أيقونة إضافية مستخدمة في التصميم
const CheckCircle2 = ({ size, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
    </svg>
);

export default TeacherQuestions;